import React, { FC, useState } from 'react';

import { OrderShort } from '@solidgate/react-sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import Api from '~/Api';
import Loader from '~/components/atoms/Loader';
import PaymentFormV3 from '~/components/organisms/SolidCheckout/components/PaymentForm/V3';
import PricingSummary from '~/modules/feature-access/components/PricingSummary';
import { getFormParams } from '~/modules/feature-access/constants';
import analytics from '~/services/analytics';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { AnalyticPaymentMethods, PAYMENT_METHODS } from '~/types/payment';
import { ProductType } from '~/types/product';

import OrderDescription from './OrderDescription';
import styles from './styles.module.scss';

type Props = {
  products: ProductType[];
  onSuccessPayment: () => void | Promise<void>;
  purchaseSource?: string;
};

const UpgradeSubModalFeatureUnlock: FC<Props> = ({
  products,
  onSuccessPayment,
  purchaseSource,
}) => {
  const { id, email } = useAppSelector((state) => state.profile);
  const { t } = useTranslation('general');

  const [isFormReady, setIsFormReady] = useState(false);

  const handlePaymentMethodChange = (type: PAYMENT_METHODS): void => {
    analytics.trackEvent('platform checkout - navigation', {
      method: type,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const handlePaymentSuccess = async (data: {
    productId: string;
    productName: string;
    order: OrderShort;
  }): Promise<void> => {
    const { productId, productName, order } = data;

    analytics.trackPurchase('platform payment - success', {
      user_id: id,
      value: order.amount ? order.amount / 100 : 0,
      product_id: productId,
      product_name: productName,
      order_id: order.order_id ? order.order_id : '',
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
      currency: order.currency,
      email,
      transaction_id: productId,
    });

    await onSuccessPayment();
  };

  const handlePaymentFail = (data: {
    productId: string;
    productName: string;
    message: string;
    method: AnalyticPaymentMethods;
  }): void => {
    const { productId, productName, message, method } = data;

    analytics.trackEvent('platform checkout - error', {
      product_id: productId,
      product_name: productName,
      method,
      error: message,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const handlePaymentSubmit = (data: {
    productId: string;
    productName: string;
    method: AnalyticPaymentMethods;
  }): void => {
    const { productId, productName, method } = data;

    analytics.trackEvent('platform checkout - submit', {
      product_id: productId,
      product_name: productName,
      method,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  const handlePaymentView = (data: {
    productId: string;
    productName: string;
  }): void => {
    const { productId, productName } = data;

    analytics.trackEvent('platform checkout - view', {
      user_id: id,
      product_id: productId,
      product_name: productName,
      ...(purchaseSource ? { purchase_source: purchaseSource } : {}),
    });
  };

  return (
    <>
      {!isFormReady && <Loader />}

      <div
        className={clsx(styles.container, {
          [styles.is_shown]: isFormReady,
        })}
      >
        <div className={styles.intro}>
          {isFormReady && <OrderDescription />}
        </div>

        <div className={styles.form}>
          <PaymentFormV3
            products={products}
            onPaymentMethodChange={handlePaymentMethodChange}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentFail={handlePaymentFail}
            onPaymentSubmit={handlePaymentSubmit}
            onPaymentView={handlePaymentView}
            onFormReady={() => setIsFormReady(true)}
            paymentApi={{
              createPaymentIntentCard: (productId, geoCountry) =>
                Api.createPaymentIntentCard(id, productId, geoCountry),
              createPaymentIntentPaypal: (productId) =>
                Api.createPaymentIntentPaypal(id, productId),
            }}
            formParams={getFormParams(t)}
            SummaryComponent={({ product }: { product: ProductType }) => (
              <PricingSummary product={product} />
            )}
          />
        </div>
      </div>
    </>
  );
};

export default UpgradeSubModalFeatureUnlock;
